import { CellOfRow } from '@components/tables';
import {
    FishCorrectModel,
    FishDisplayModel,
    FishInstallModel,
    H264Profile,
    RateMode,
    RateQuality,
    StreamEnable,
    TvStandards,
    VbrQuality,
    VideoCodec,
} from '@EcamModel/model/PTZVideoGeneral';
import { Stack, TableBody, TableRow, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import {
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionNotSubItem,
    TableHeaderStyle,
} from './BasicInfoMilesightCameraPTZ';
import useMileSightPTZ, { SectionsCollapsePTZ } from '@pages/cameras/hook/useMilesightPTZ';
import { useEffect } from 'react';

const tvStandardsMapping: { [key in TvStandards]: string } = {
    [TvStandards.Off]: 'Off',
    [TvStandards.NTSC]: 'NTSC',
    [TvStandards.PAL]: 'PAL',
};

const rateModeMapping: { [key in RateMode]: string } = {
    [RateMode.CBR]: 'Constant Bit Rate (CBR)',
    [RateMode.VBR]: 'Variable Bit Rate (VBR)',
};

const videoCodecMapping: { [key in VideoCodec]: string } = {
    [VideoCodec.H264]: 'H.264',
    [VideoCodec.MJPEG]: 'MJPEG',
    [VideoCodec.H265]: 'H.265',
};

const h264ProfileMapping: { [key in H264Profile]: string } = {
    [H264Profile.BaseProfile]: 'Base',
    [H264Profile.MainProfile]: 'Main',
    [H264Profile.HighProfile]: 'High',
};

const rateQualityMapping: { [key in RateQuality]: string } = {
    [RateQuality.Low]: '25',
    [RateQuality.Medium]: '50',
    [RateQuality.High]: '60',
    [RateQuality.Higher]: '75',
};

const vbrQualityMapping: { [key in VbrQuality]: string } = {
    [VbrQuality.Low]: 'Low',
    [VbrQuality.Medium]: 'Medium',
    [VbrQuality.High]: 'High',
};

const streamEnableMapping: { [key in StreamEnable]: string } = {
    [StreamEnable.Disable]: 'Disable',
    [StreamEnable.Enable]: 'Enable',
};

const fishDisplayModelMapping: { [key in FishDisplayModel]: string } = {
    [FishDisplayModel.Model10]: 'Model 1.0',
    [FishDisplayModel.Model1P]: 'Model 1P',
    [FishDisplayModel.Model2P]: 'Model 2P',
    [FishDisplayModel.Model2R]: 'Model 2R',
    [FishDisplayModel.Model4R]: 'Model 4R',
    [FishDisplayModel.Model1O3R]: 'Model 1O3R',
    [FishDisplayModel.Model1P3R]: 'Model 1P3R',
    [FishDisplayModel.Model1O1P3R]: 'Model 1O1P3R',
};

const fishInstallModelMapping: { [key in FishInstallModel]: string } = {
    [FishInstallModel.Ceiling]: 'Ceiling',
    [FishInstallModel.Wall]: 'Wall',
    [FishInstallModel.Flat]: 'Flat',
};

const fishCorrectModelMapping: { [key in FishCorrectModel]: string } = {
    [FishCorrectModel.MultiChannelMode]: 'Multi-Channel Mode',
    [FishCorrectModel.BundleStreamMode]: 'Bundle Stream Mode',
};

export interface TableRowData {
    id: number;
    label: string;
    value: string | number;
    altValue?: string | number;
}

const tableHeaders: TableHeaderStyle[] = [
    {
        label: 'No.',
        align: 'left',
    },
    {
        label: 'Name',
        align: 'left',
    },
    {
        label: 'General',
        align: 'left',
    },
    {
        label: 'Event',
        align: 'left',
    },
];

export const labels: string[] = [
    'Enable',
    'Video Codec',
    'Frame Size',
    'Maximum Frame Rate',
    'Bit Rate',
    'Smart Stream',
    'Bit Rate Control',
    'Profile',
    'I-frame Interval',
];

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePTZ]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePTZ) => void;
};

export default function VideoMilesightsCameraPTZ(props: Props) {
    const { camera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const PTZVideoGeneral = camera?.PTZCamera?.PTZCameraConfig?.VideoGeneral;

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const statusList_FirstStream: TableRowData[] = [
        { id: 1, label: labels[0], value: PTZVideoGeneral?.EventStreamEnable ? 'Yes' : 'No' },
        {
            id: 2,
            label: labels[1],
            value: videoCodecMapping[PTZVideoGeneral?.StreamList?.MainStream?.ProfileCodec ?? 0],
        },
        {
            id: 3,
            label: labels[2],
            value: `${PTZVideoGeneral?.StreamList?.MainStream?.Width ?? ''}*${
                PTZVideoGeneral?.StreamList?.MainStream?.Height ?? ''
            }`,
        },
        {
            id: 4,
            label: labels[3],
            value: `${PTZVideoGeneral?.StreamList?.MainStream?.Framerate ?? 0} Fps`,
            altValue: `${PTZVideoGeneral?.EventStreamFramerate} Fps`,
        },
        {
            id: 5,
            label: labels[4],
            value: `${PTZVideoGeneral?.StreamList?.MainStream?.Bitrate ?? 0} Kbps`,
            altValue: `${PTZVideoGeneral?.EventStreamBitrate} Kbps`,
        },
        {
            id: 6,
            label: labels[5],
            value: PTZVideoGeneral?.StreamList?.MainStream?.SmartStreamEnable ? 'On' : 'Off',
        },
        {
            id: 7,
            label: labels[6],
            value: rateModeMapping[PTZVideoGeneral?.StreamList?.MainStream?.RateMode ?? 0],
        },
        {
            id: 8,
            label: labels[7],
            value: h264ProfileMapping[PTZVideoGeneral?.StreamList?.MainStream?.Profile ?? 0],
        },
        {
            id: 9,
            label: labels[8],
            value: PTZVideoGeneral?.StreamList?.MainStream?.ProfileGop ?? 0,
        },
    ];

    const statusList_SecondaryStream: DataSectionHasSubItem[] = [
        {
            label: labels[0],
            value: <Typography>{PTZVideoGeneral?.StreamList?.SubStream?.Enable ? 'Yes' : 'No'}</Typography>,
        },
        {
            label: labels[1],
            value: (
                <Typography>
                    {videoCodecMapping[PTZVideoGeneral?.StreamList?.SubStream?.ProfileCodec ?? 'N/A']}
                </Typography>
            ),
        },
        {
            label: labels[2],
            value: (
                <Typography>{`${PTZVideoGeneral?.StreamList?.SubStream?.Width} x ${PTZVideoGeneral?.StreamList?.SubStream?.Height}`}</Typography>
            ),
        },
        {
            label: labels[3],
            value: <Typography>{`${PTZVideoGeneral?.StreamList?.SubStream?.Framerate ?? 0} fps`}</Typography>,
        },
        {
            label: labels[4],
            value: <Typography>{`${PTZVideoGeneral?.StreamList?.SubStream?.Bitrate ?? 0} Kbps`}</Typography>,
        },
        {
            label: labels[5],
            value: <Typography>{PTZVideoGeneral?.StreamList?.SubStream?.SmartStreamEnable ? 'On' : 'Off'}</Typography>,
        },
        {
            label: labels[6],
            value: (
                <Typography>{rateModeMapping[PTZVideoGeneral?.StreamList?.SubStream?.RateMode ?? 'N/A']}</Typography>
            ),
        },
        {
            label: labels[7],
            value: (
                <Typography>{h264ProfileMapping[PTZVideoGeneral?.StreamList?.SubStream?.Profile ?? 'N/A']}</Typography>
            ),
        },
        {
            label: labels[8],
            value: (
                <Typography>{`${
                    rateQualityMapping[PTZVideoGeneral?.StreamList?.SubStream?.RateQuality ?? 'N/A']
                } frame (1-120)`}</Typography>
            ),
        },
    ];

    const statusList_TertiaryStream: DataSectionHasSubItem[] = [
        {
            label: labels[0],
            value: <Typography>{PTZVideoGeneral?.StreamList?.ThirdStream?.Enable ? 'Yes' : 'No'}</Typography>,
        },
        {
            label: labels[1],
            value: (
                <Typography>
                    {videoCodecMapping[PTZVideoGeneral?.StreamList?.ThirdStream?.ProfileCodec ?? 'N/A']}
                </Typography>
            ),
        },
        {
            label: labels[2],
            value: (
                <Typography>{`${PTZVideoGeneral?.StreamList?.ThirdStream?.Width} x ${PTZVideoGeneral?.StreamList?.ThirdStream?.Height}`}</Typography>
            ),
        },
        {
            label: labels[3],
            value: <Typography>{`${PTZVideoGeneral?.StreamList?.ThirdStream?.Framerate} fps`}</Typography>,
        },
        {
            label: labels[4],
            value: <Typography>{`${PTZVideoGeneral?.StreamList?.ThirdStream?.Bitrate} Kbps`}</Typography>,
        },
        {
            label: labels[5],
            value: (
                <Typography>{PTZVideoGeneral?.StreamList?.ThirdStream?.SmartStreamEnable ? 'On' : 'Off'}</Typography>
            ),
        },
        {
            label: labels[6],
            value: (
                <Typography>{rateModeMapping[PTZVideoGeneral?.StreamList?.ThirdStream?.RateMode ?? 'N/A']}</Typography>
            ),
        },
        {
            label: labels[7],
            value: (
                <Typography>
                    {h264ProfileMapping[PTZVideoGeneral?.StreamList?.ThirdStream?.Profile ?? 'N/A']}
                </Typography>
            ),
        },
        {
            label: labels[8],
            value: (
                <Typography>{`${
                    rateQualityMapping[PTZVideoGeneral?.StreamList?.ThirdStream?.RateQuality ?? 'N/A']
                } frame (1-120)`}</Typography>
            ),
        },
    ];

    return (
        <Stack id="primaryStream" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Video */}
            <SectionHasSubItemHeader id="" title="Video" onEdit={handleEdit} />

            <SectionNotSubItem
                header="Primary stream"
                tableHeaders={tableHeaders}
                isOpen={isOpenCollapse.primaryStream}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.PrimaryStream);
                }}
                items={statusList_FirstStream || []}
                tableBody={
                    <TableBody>
                        {statusList_FirstStream.map((row) => (
                            <TableRow key={row.id}>
                                <CellOfRow value={row.id} align="left" />
                                <CellOfRow value={row.label} align="left" />
                                <CellOfRow value={row.value} align="left" />
                                <CellOfRow value={row.altValue || row.value} align="left" />
                            </TableRow>
                        ))}
                    </TableBody>
                }
            />

            <SectionNotSubItem
                name="secondaryStream"
                header="Secondary stream"
                statusList={statusList_SecondaryStream}
                isOpen={isOpenCollapse.secondaryStream}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.SecondaryStream);
                }}
            />

            <SectionNotSubItem
                name="tertiaryStream"
                header="Tertiary stream"
                statusList={statusList_TertiaryStream}
                isOpen={isOpenCollapse.tertiaryStream}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapsePTZ.TertiaryStream);
                }}
            />
        </Stack>
    );
}
